import { useEffect, useState } from "react";

// STYLES
import * as Style from "./SupportStyle";

// LIBRARIES
import { useTranslation } from "react-i18next";

// MISC
import { IModalData } from "components/Atoms/ModalAtom/ModalAtomModel";
import { ButtonTypes, InputTypes, ModalTypes, SnackbarTypes } from "models/GeneralTypes/generalTypes";
import {
  IArticle,
  ICategory, IEditArticlePayload,
  IGetParentChildren,
  ISubcategory
} from "models/interfaces";

// ASSETS
import AddIcon from "assets/Icons/AddSupport.svg";
import TestSvgAsComponent from "assets/Icons/Arrow Left.svg";

// REDUX
import {
  deleteArticle,
  editArticle,
  getAllCategories,
  getCategorySubcategories,
  getSubcategoryArticles
} from "api/supportApi";
import { useAppDispatch, useAppSelector } from "hooks/reduxHooks";
import { language, toggleLanguageState } from "slices/sessionSlice";
import { articles, categories, subcategories } from "slices/supportSlice";
import { modalProps, toggleModalState, toggleSnackbarState } from "slices/uiSlice";

// COMPONENTS
import ButtonAtom from "components/Atoms/ButtonAtom/ButtonAtom";
import InputAtom from "components/Atoms/InputAtom/InputAtom";
import TextEditor from "components/TextEditor/TextEditor";

const Support = () => {
  // PROPS

  // CONSTANTS USING LIBRARIES
  const dispatch = useAppDispatch();
  const selectedLanguage = useAppSelector<string>(language);
  const modalData = useAppSelector<IModalData>(modalProps);
  const allCategories = useAppSelector<ICategory[]>(categories);
  const categorySubcategories = useAppSelector<ISubcategory[]>(subcategories);
  const subcategoryArticles = useAppSelector<IArticle[]>(articles);
  const { t } = useTranslation();

  // CONSTANTS USING HOOKS
  const [inputValue, setInputValue] = useState(modalData?.article?.title);
  const [articleContent, setArticleContent] = useState(modalData?.article?.content);

  // GENERAL CONSTANTS

  // USE EFFECT FUNCTION
  useEffect(() => {
    dispatch(
      toggleModalState({
        ...modalData,
        category: null,
        subcategory: null,
        article: null,
      })
    );
    dispatch(getAllCategories(selectedLanguage)).catch((error) => {
      console.log(error);
    });
    // eslint-disable-next-line
  }, []);

  // REQUEST API FUNCTIONS

  // HANDLERS FUNCTIONS
  const handleToggleLanguage = () => {
    if (selectedLanguage === "ro") {
      let firstPayload: IGetParentChildren = {
        id: modalData?.category?.id || 0,
        language: "en",
      };
      let secondPayload: IGetParentChildren = {
        id: modalData?.subcategory?.id || 0,
        language: "en",
      };
      dispatch(toggleLanguageState("en"));
      dispatch(getAllCategories("en"));
      dispatch(getCategorySubcategories(firstPayload));
      dispatch(getSubcategoryArticles(secondPayload));
    } else {
      let firstPayload: IGetParentChildren = {
        id: modalData?.category?.id || 0,
        language: "ro",
      };
      let secondPayload: IGetParentChildren = {
        id: modalData?.subcategory?.id || 0,
        language: "ro",
      };
      dispatch(toggleLanguageState("ro"));
      dispatch(getAllCategories("ro"));
      dispatch(getCategorySubcategories(firstPayload));
      dispatch(getSubcategoryArticles(secondPayload));
    }
    dispatch(
      toggleModalState({
        ...modalData,
        category: null,
        subcategory: null,
        article: null,
      })
    );
  };

  const handleSelectCategory = (category: ICategory) => {
    let payload: IGetParentChildren = {
      id: category.id,
      language: selectedLanguage,
    };
    console.log({ payload })
    dispatch(getCategorySubcategories(payload))
      .catch((error) => {
        console.log(error);
      })
      .then(() => {
        dispatch(
          toggleModalState({
            ...modalData,
            category: category,
            subcategory: null,
            article: null,
          })
        );
      })
      .then(() => {
        dispatch(getSubcategoryArticles(null));
      });
  };

  const handleSelectSubcategory = (subcategory: ISubcategory) => {
    let payload: IGetParentChildren = {
      id: subcategory.id,
      language: selectedLanguage,
    };
    dispatch(getSubcategoryArticles(payload))
      .catch((error) => {
        console.log(error);
      })
      .then(() => {
        dispatch(
          toggleModalState({
            ...modalData,
            subcategory: subcategory,
            article: null,
          })
        );
      });
  };

  const handleEditCategory = (event) => {
    event.stopPropagation();
    dispatch(
      toggleModalState({
        ...modalData,
        isVisible: true,
        type: ModalTypes.EDIT_CATEGORY,
      })
    );
  };

  const handleEditSubcategory = (event) => {
    event.stopPropagation();
    dispatch(
      toggleModalState({
        ...modalData,
        isVisible: true,
        type: ModalTypes.EDIT_SUBCATEGORY,
      })
    );
  };

  const handleSelectArticle = (article) => {
    console.log("Article", article)
    dispatch(
      toggleModalState({
        ...modalData,
        article: article,
      })
    );
    setArticleContent(article.content);
    setInputValue(article.title);
  };

  const handleAddCategory = () => {
    dispatch(
      toggleModalState({
        ...modalData,
        isVisible: true,
        type: ModalTypes.ADD_CATEGORY,
      })
    );
  };

  const handleEditArticle = () => {
    let editArticlePayload: IEditArticlePayload = {
      language: selectedLanguage,
      data: {
        content: articleContent,
        id: modalData.article.id,
        subcategoryId: modalData.article.subcategory.id,
        title: inputValue,
      }
    }
    console.log("test", editArticlePayload)
    dispatch(editArticle(editArticlePayload))
      .unwrap()
      .then((response) => {
        console.log("response", response);
        console.log("article data", modalData.article)
        let payload: IGetParentChildren = {
          id: modalData.subcategory.id,
          language: selectedLanguage
        };
        dispatch(getSubcategoryArticles(payload))
        dispatch(toggleSnackbarState({
          isVisible: true,
          type: SnackbarTypes.SUPPORT_EDIT_SUCCESS,
          item: t("snackbar.article"),
        }))
      });
  }

  const handleCancelEditArticle = () => {
    setInputValue(modalData?.article?.title)
    setArticleContent(modalData?.article?.content)
  }

  const handleDeleteArticle = () => {
    dispatch(deleteArticle(modalData.article.id))
      .then(() => {
        let payload: IGetParentChildren = {
          id: modalData.subcategory.id,
          language: selectedLanguage
        };
        dispatch(getSubcategoryArticles(payload))
          .then(() => {
            dispatch(
              toggleModalState({
                ...modalData,
                article: null,
              })
            );
            dispatch(toggleSnackbarState({
              isVisible: true,
              type: SnackbarTypes.SUPPORT_DELETE_SUCCESS,
              item: t("snackbar.article"),
            }))
          })
      });
  }

  return (
    <Style.SupportContainer>
      <Style.LeftSideContainer>
        <Style.LanguageToggleWrapper>
          <Style.LanguageValue1 onClick={() => handleToggleLanguage()} selected={selectedLanguage === "en"}>
            {t("modal.language.english")}
          </Style.LanguageValue1>
          <Style.LanguageValue onClick={() => handleToggleLanguage()} selected={selectedLanguage === "ro"}>
            {t("modal.language.romanian")}
          </Style.LanguageValue>
        </Style.LanguageToggleWrapper>
        <Style.AddCategoryWrapper>

          <Style.AddCategory onClick={() => handleAddCategory()}>
            <Style.ImageContainer src={AddIcon} alt="" />
            {t("support.addSupportItem")}
          </Style.AddCategory>
        </Style.AddCategoryWrapper>
        {allCategories.length > 0 && (
          <>
            {allCategories.map((item, index) => (
              <>
                <Style.Category selected={item?.id === modalData?.category?.id} onClick={() => handleSelectCategory(item)}>
                  <Style.CategoryTitle>
                    {item.name || ""}
                  </Style.CategoryTitle>
                  <Style.EditWrapper>
                    {item?.id === modalData?.category?.id && <Style.Edit onClick={handleEditCategory}>
                      {t("support.edit")}
                    </Style.Edit>}
                    <Style.ArrowWrapper rotate={item?.id === modalData?.category?.id}>
                      <Style.ArrowContainer
                        src={TestSvgAsComponent}
                      />
                    </Style.ArrowWrapper>
                  </Style.EditWrapper>
                </Style.Category>
                {item?.id === modalData?.category?.id &&
                  <>
                    {categorySubcategories.map((item, index) => (
                      <>

                        <Style.Subcategory>
                          <Style.EditWrapperSubcategory>
                            <Style.SubcategoryTitle selected={item?.id === modalData?.subcategory?.id} onClick={() => handleSelectSubcategory(item)}>
                              {item.name || ""}
                            </Style.SubcategoryTitle>
                            {item?.id === modalData?.subcategory?.id && <Style.EditSubcategory onClick={handleEditSubcategory}>
                              {t("support.edit")}
                            </Style.EditSubcategory>}
                          </Style.EditWrapperSubcategory>
                        </Style.Subcategory>
                        {item?.id === modalData?.subcategory?.id &&
                          subcategoryArticles.map((item, index) => (
                            <Style.Article>
                              <Style.ArticleTitle selected={item?.id === modalData?.article?.id} onClick={() => handleSelectArticle(item)}>
                                - {item.title || ""}
                              </Style.ArticleTitle>
                            </Style.Article>
                          ))
                        }
                      </>
                    ))}
                  </>
                }
              </>
            ))}
          </>
        )}
      </Style.LeftSideContainer>

      <Style.RightSideContainer>
        <Style.Header>
          <Style.ButtonHeaderContainer>
            {modalData?.article &&
              <ButtonAtom
                text={t("component.lockerFunctionality.button.title.delete")}
                onClick={() => handleDeleteArticle()}
                type={ButtonTypes.SECONDARY_BUTTON_DEFAULT}
              />}
          </Style.ButtonHeaderContainer>
        </Style.Header>
        <Style.ArticleWrapper>
          {(modalData?.article?.content?.length > 0 || modalData?.article?.title?.length > 0) && (
            <>
              <Style.InputAtomContainer>


                <Style.UserName>{t("support.articleName")}</Style.UserName>
                <InputAtom
                  placeholder={t("page.pushNotifications.title")}
                  hasLabel={true}
                  inputValue={inputValue}
                  setInputValue={setInputValue}
                  type={InputTypes.MODAL_INPUT}
                />
              </Style.InputAtomContainer>
            </>
          )}
          {(modalData?.article?.content?.length > 0 || modalData?.article?.title?.length > 0) && (
            <Style.ArticleDescriptionWrapper>


              <Style.UserName>{t("support.description")}</Style.UserName>
              <TextEditor value={articleContent} setValue={setArticleContent} height={350} />
            </Style.ArticleDescriptionWrapper>
          )}
          {(modalData?.article?.content?.length > 0 || modalData?.article?.title?.length > 0) &&
            <Style.ButtonsWrapper>
              <Style.ButtonContainer>
                <ButtonAtom
                  text={t("support.cancel")}
                  onClick={handleCancelEditArticle}
                  type={ButtonTypes.SECONDARY_BUTTON_DEFAULT}

                />
              </Style.ButtonContainer>

              <Style.ButtonContainer>
                <ButtonAtom
                  text={t("support.save")}
                  onClick={handleEditArticle}
                  type={ButtonTypes.SECONDARY_BUTTON_DEFAULT}

                />
              </Style.ButtonContainer>

            </Style.ButtonsWrapper>
          }

        </Style.ArticleWrapper>
      </Style.RightSideContainer>
    </Style.SupportContainer>
  );
};

export default Support;
